import React, { useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import engineeringMaterialService, {
  type EngineeringGrade,
  type EngineeringSection,
  type EngineeringStatus,
} from '@/services/engineeringMaterial.service';
import { useAuthStore } from '@/store/authStore';

const { Title, Text } = Typography;

const STATUS_COLOR: Record<EngineeringStatus, string> = { DRAFT: 'gold', APPROVED: 'green', INACTIVE: 'default' };

const errorText = (e: any, fallback: string) => e?.response?.data?.message || e?.message || fallback;

export const EngineeringMaterialsPage: React.FC = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const perms = user?.permissions ?? [];
  const isAdmin = Boolean(user?.is_super_admin);
  const canApprove = isAdmin || perms.includes('inventory.engineering.approve');
  const canManage = canApprove || perms.includes('inventory.engineering.manage');

  const [showInactive, setShowInactive] = useState(false);
  const [gradeForm] = Form.useForm();
  const [sectionForm] = Form.useForm();
  const [gradeEditing, setGradeEditing] = useState<EngineeringGrade | null>(null);
  const [sectionEditing, setSectionEditing] = useState<EngineeringSection | null>(null);
  const [gradeOpen, setGradeOpen] = useState(false);
  const [sectionOpen, setSectionOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: config } = useQuery({ queryKey: ['engineering', 'config'], queryFn: () => engineeringMaterialService.getConfig() });
  const { data: grades = [], isLoading: gradesLoading } = useQuery({
    queryKey: ['engineering', 'grades', showInactive],
    queryFn: () => engineeringMaterialService.listGrades(showInactive),
  });
  const { data: sections = [], isLoading: sectionsLoading } = useQuery({
    queryKey: ['engineering', 'sections', showInactive],
    queryFn: () => engineeringMaterialService.listSections(showInactive),
  });

  const publishedProfiles = (config?.profiles ?? []).filter((p) => p.methods.includes('PUBLISHED'));
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['engineering'] });

  const toggleEnabled = async (enabled: boolean) => {
    try {
      await engineeringMaterialService.setEnabled(enabled);
      message.success(enabled ? 'Engineering weight enabled' : 'Engineering weight disabled');
      await refresh();
    } catch (e) {
      message.error(errorText(e, 'Could not change the setting'));
    }
  };

  const openGrade = (row: EngineeringGrade | null) => {
    setGradeEditing(row);
    gradeForm.resetFields();
    if (row) gradeForm.setFieldsValue({ ...row, density_g_cm3: Number(row.density_g_cm3) });
    setGradeOpen(true);
  };

  const saveGrade = async () => {
    try {
      const v = await gradeForm.validateFields();
      setSaving(true);
      if (gradeEditing) await engineeringMaterialService.updateGrade(gradeEditing.id, v);
      else await engineeringMaterialService.createGrade(v);
      message.success(gradeEditing ? 'Grade updated' : 'Grade created (Draft — approve it before use)');
      setGradeOpen(false);
      await refresh();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(errorText(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const openSection = (row: EngineeringSection | null) => {
    setSectionEditing(row);
    sectionForm.resetFields();
    if (row) sectionForm.setFieldsValue({ ...row, kg_per_m: Number(row.kg_per_m) });
    setSectionOpen(true);
  };

  const saveSection = async () => {
    try {
      const v = await sectionForm.validateFields();
      setSaving(true);
      if (sectionEditing) await engineeringMaterialService.updateSection(sectionEditing.id, v);
      else await engineeringMaterialService.createSection(v);
      message.success(sectionEditing ? 'Section updated' : 'Section created (Draft — approve it before use)');
      setSectionOpen(false);
      await refresh();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(errorText(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (kind: 'grade' | 'section', id: string, status: 'APPROVED' | 'INACTIVE') => {
    try {
      if (kind === 'grade') await engineeringMaterialService.setGradeStatus(id, status);
      else await engineeringMaterialService.setSectionStatus(id, status);
      message.success(status === 'APPROVED' ? 'Approved' : 'Deactivated');
      await refresh();
    } catch (e) {
      message.error(errorText(e, 'Status change failed'));
    }
  };

  const statusActions = (kind: 'grade' | 'section', row: { id: string; status: EngineeringStatus }) =>
    canApprove ? (
      <>
        {row.status !== 'APPROVED' && (
          <Popconfirm title="Approve this value for use on items and documents?" onConfirm={() => setStatus(kind, row.id, 'APPROVED')}>
            <Button size="small" type="link">
              Approve
            </Button>
          </Popconfirm>
        )}
        {row.status !== 'INACTIVE' && (
          <Popconfirm
            title="Deactivate?"
            description="Existing approved item definitions keep their frozen values."
            onConfirm={() => setStatus(kind, row.id, 'INACTIVE')}
          >
            <Button size="small" type="link" danger>
              Deactivate
            </Button>
          </Popconfirm>
        )}
      </>
    ) : null;

  const gradeColumns: ColumnsType<EngineeringGrade> = [
    { title: 'Family', dataIndex: 'family', width: 160 },
    { title: 'Grade', dataIndex: 'grade_code', width: 140 },
    { title: 'Density (g/cm³)', dataIndex: 'density_g_cm3', width: 130, align: 'right', render: (v) => Number(v).toFixed(4) },
    { title: 'Density source', dataIndex: 'density_source', ellipsis: true, render: (v) => v || '—' },
    { title: 'Status', dataIndex: 'status', width: 100, render: (s: EngineeringStatus) => <Tag color={STATUS_COLOR[s]}>{s}</Tag> },
    {
      title: 'Actions',
      key: 'actions',
      width: 230,
      render: (_, row) => (
        <Space size={0}>
          {canManage && (
            <Button size="small" type="link" onClick={() => openGrade(row)}>
              Edit
            </Button>
          )}
          {statusActions('grade', row)}
        </Space>
      ),
    },
  ];

  const profileLabel = (code: string) => config?.profiles.find((p) => p.code === code)?.label ?? code;

  const sectionColumns: ColumnsType<EngineeringSection> = [
    { title: 'Profile', dataIndex: 'profile_code', width: 140, render: profileLabel },
    { title: 'Designation', dataIndex: 'designation', width: 180 },
    { title: 'Standard', dataIndex: 'standard', width: 140, render: (v) => v || '—' },
    { title: 'kg/m', dataIndex: 'kg_per_m', width: 100, align: 'right', render: (v) => Number(v).toFixed(4) },
    { title: 'Source', dataIndex: 'source_reference', ellipsis: true, render: (v) => v || '—' },
    { title: 'Status', dataIndex: 'status', width: 100, render: (s: EngineeringStatus) => <Tag color={STATUS_COLOR[s]}>{s}</Tag> },
    {
      title: 'Actions',
      key: 'actions',
      width: 230,
      render: (_, row) => (
        <Space size={0}>
          {canManage && (
            <Button size="small" type="link" onClick={() => openSection(row)}>
              Edit
            </Button>
          )}
          {statusActions('section', row)}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
            <Title level={3} style={{ margin: 0 }}>
              Engineering materials &amp; weights
            </Title>
            <Space>
              <Text>Theoretical weight on Purchase Requests</Text>
              <Switch
                checked={Boolean(config?.enabled)}
                disabled={!canApprove || !config?.available}
                onChange={toggleEnabled}
              />
            </Space>
          </Space>
          {config && !config.available && (
            <Alert type="warning" showIcon message="The engineering tables are not installed yet (migration 532 pending)." />
          )}
          <Text type="secondary">
            Material densities and published section weights are reference data. Enter only values from a cited source
            (standard, mill certificate, supplier catalogue) and approve them before use. Approved item definitions freeze
            these values, so later edits never change saved documents. Theoretical weight is information only: it never
            changes quantities, stock or valuation. Formula revision {config?.formula_revision ?? '—'}.
          </Text>
          <Space>
            <Switch size="small" checked={showInactive} onChange={setShowInactive} />
            <Text>Show inactive</Text>
          </Space>
          <Tabs
            items={[
              {
                key: 'grades',
                label: 'Material grades',
                children: (
                  <>
                    {canManage && (
                      <Button type="primary" icon={<PlusOutlined />} onClick={() => openGrade(null)} style={{ marginBottom: 12 }}>
                        New grade
                      </Button>
                    )}
                    <Table<EngineeringGrade> rowKey="id" size="small" loading={gradesLoading} columns={gradeColumns} dataSource={grades} />
                  </>
                ),
              },
              {
                key: 'sections',
                label: 'Published section weights',
                children: (
                  <>
                    {canManage && (
                      <Button type="primary" icon={<PlusOutlined />} onClick={() => openSection(null)} style={{ marginBottom: 12 }}>
                        New section weight
                      </Button>
                    )}
                    <Table<EngineeringSection>
                      rowKey="id"
                      size="small"
                      loading={sectionsLoading}
                      columns={sectionColumns}
                      dataSource={sections}
                    />
                  </>
                ),
              },
            ]}
          />
        </Space>
      </Card>

      <Modal
        title={gradeEditing ? 'Edit material grade' : 'New material grade'}
        open={gradeOpen}
        onCancel={() => setGradeOpen(false)}
        onOk={saveGrade}
        confirmLoading={saving}
        destroyOnClose
      >
        {gradeEditing?.status === 'APPROVED' && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Changing the grade code or density returns this grade to Draft until it is approved again."
          />
        )}
        <Form form={gradeForm} layout="vertical">
          <Form.Item name="family" label="Material family" rules={[{ required: true }]}>
            <Input placeholder="e.g. Carbon steel, Stainless steel, Aluminium" maxLength={60} />
          </Form.Item>
          <Form.Item name="grade_code" label="Grade" rules={[{ required: true }]}>
            <Input placeholder="e.g. S275JR, SS304, 6061-T6" maxLength={60} />
          </Form.Item>
          <Form.Item
            name="density_g_cm3"
            label="Density (g/cm³)"
            rules={[{ required: true, message: 'Density is required' }]}
            extra="Use the value from the cited source. Example: 7.85 for carbon steel per EN 10025."
          >
            <InputNumber min={0.0001} max={25} step={0.01} precision={4} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="density_source" label="Density source" rules={[{ required: true, message: 'Cite the source' }]}>
            <Input placeholder="Standard / certificate / catalogue reference" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={sectionEditing ? 'Edit section weight' : 'New published section weight'}
        open={sectionOpen}
        onCancel={() => setSectionOpen(false)}
        onOk={saveSection}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={sectionForm} layout="vertical">
          <Form.Item name="profile_code" label="Profile" rules={[{ required: true }]}>
            <Select options={publishedProfiles.map((p) => ({ value: p.code, label: p.label }))} />
          </Form.Item>
          <Form.Item name="designation" label="Designation" rules={[{ required: true }]}>
            <Input placeholder="e.g. IPE 200, UPN 100, L 50x50x5" maxLength={100} />
          </Form.Item>
          <Form.Item name="standard" label="Standard">
            <Input placeholder="e.g. EN 10365" maxLength={100} />
          </Form.Item>
          <Form.Item name="kg_per_m" label="Mass per metre (kg/m)" rules={[{ required: true }]}>
            <InputNumber min={0.0001} step={0.1} precision={4} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="source_reference" label="Source reference" rules={[{ required: true, message: 'Cite the source' }]}>
            <Input placeholder="Table / catalogue page" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EngineeringMaterialsPage;
